import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  AutoEmailKey,
  AutoEmailPreview,
  AutoEmailSave,
  AutoEmailTemplate,
  useGetAutoEmailsQuery,
  useGetNewFirmsQuery,
  usePreviewAutoEmailMutation,
  useSaveAutoEmailMutation,
  useTestAutoEmailMutation,
} from "../../slices/SupportSlice";
import {
  autoEmailProblem,
  autoEmailWhen,
  CASE_EMAIL_KEYS,
  DEMO_EMAIL_KEYS,
  errorText,
  insertAt,
  ONBOARDING_KEYS,
  PILL_CLASSES,
  TIP_LABELS,
} from "./support.utils";
import { EmailPreviewModal } from "./EmailPreviewModal";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

const BackToHelpDesk = () => (
  <Link
    to={"/support"}
    className={"text-[13px] font-semibold text-[#6B7185] hover:text-[#6B4EFF]"}>
    ‹ Help desk
  </Link>
);

/** Help desk › Automatic emails (owner 2026-10-25): approved once, then sent by themselves. */
export const AutoEmailsPage = () => {
  const { data, isLoading, isError } = useGetAutoEmailsQuery();
  if (isLoading) return <LottieLoading />;
  const byKey = new Map((data || []).map((t) => [t.key, t]));
  const group = (title: string, keys: AutoEmailKey[]) => (
    <section className={"mb-5"}>
      <h2
        className={
          "mb-2 text-[12px] font-bold uppercase tracking-[.5px] text-[#5D6475]"
        }>
        {title}
      </h2>
      <div className={"flex flex-col gap-2.5"}>
        {keys.map((key) => {
          const t = byKey.get(key);
          if (!t) return null;
          return (
            <Link
              key={key}
              to={`/support/automatic-emails/${key}`}
              className={
                "flex items-center gap-3 rounded-[14px] border border-[#EDEFF3] bg-white px-4 py-3.5 hover:border-[#D9D0FF]"
              }>
              <span
                className={
                  "w-[70px] shrink-0 text-[12.5px] font-bold text-[#6B4EFF]"
                }>
                {autoEmailWhen(t)}
              </span>
              <span className={"min-w-0 flex-1"}>
                <span className={"block text-[14px] font-bold text-[#1F2433]"}>
                  {t.name}
                </span>
                <span className={"block text-[12.5px] text-[#5D6475]"}>
                  {t.when}
                </span>
              </span>
              <span
                className={classNames(
                  "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                  !t.enabled
                    ? PILL_CLASSES.grey
                    : t.draftOnly
                    ? PILL_CLASSES.purple
                    : PILL_CLASSES.green,
                )}>
                {!t.enabled ? "Off" : t.draftOnly ? "Draft" : "On"}
              </span>
              <span className={"text-[13px] font-semibold text-[#6B4EFF]"}>
                Edit ›
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
  return (
    <div className={PAGE}>
      <div className={classNames(PAGE_INNER, "max-w-[860px]")}>
        <BackToHelpDesk />
        <h1
          className={
            "mb-1 mt-2 text-[24px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
          }>
          Automatic emails
        </h1>
        <p className={"mb-5 text-[14px] text-[#5D6475]"}>
          You approve these texts once; after that they go out by themselves
          from support@procharted.com. Answers to customers&apos; questions
          still wait for your Send.
        </p>
        {isError ? (
          <div className={"text-[14px] text-[#B42318]"}>
            The automatic emails couldn&apos;t be loaded.
          </div>
        ) : (
          <>
            {group("New firms", ONBOARDING_KEYS)}
            {group("Demos", DEMO_EMAIL_KEYS)}
            {group("Support cases", CASE_EMAIL_KEYS)}
          </>
        )}
      </div>
    </div>
  );
};

/** One automatic email: when, on/off, subject, text with fill-ins, the Day 3 tips, preview, test, save. */
export const AutoEmailEditor = () => {
  const { key = "" } = useParams();
  const { data, isLoading } = useGetAutoEmailsQuery();
  const template = data?.find((t) => t.key === key);
  if (isLoading) return <LottieLoading />;
  if (!template)
    return (
      <div className={PAGE}>
        <div className={PAGE_INNER}>
          <BackToHelpDesk />
          <div className={"mt-8 text-center text-[15px] text-[#6B7185]"}>
            This automatic email doesn&apos;t exist.
          </div>
        </div>
      </div>
    );
  return (
    <EditorBody
      key={template.key + (template.updatedAt || "")}
      template={template}
    />
  );
};

type FocusTarget =
  | { kind: "subject" }
  | { kind: "body" }
  | { kind: "tip"; key: string };

const EditorBody = ({ template }: { template: AutoEmailTemplate }) => {
  const [enabled, setEnabled] = useState(template.enabled);
  const [delay, setDelay] = useState(String(template.delayDays));
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [tips, setTips] = useState<Record<string, string>>(template.tips || {});
  const [link, setLink] = useState(template.linkUrl || "");
  const [previewFirm, setPreviewFirm] = useState("");
  const [preview, setPreview] = useState<AutoEmailPreview | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [save, { isLoading: isSaving }] = useSaveAutoEmailMutation();
  const [runPreview, { isLoading: isPreviewing }] =
    usePreviewAutoEmailMutation();
  const [sendTest, { isLoading: isTesting }] = useTestAutoEmailMutation();
  const { data: firms } = useGetNewFirmsQuery();
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const tipRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const lastFocus = useRef<FocusTarget>({ kind: "body" });

  const form: AutoEmailSave = useMemo(
    () => ({
      enabled,
      ...(template.hasDelay ? { delayDays: Number(delay) } : {}),
      ...(template.hasSubject ? { subject } : {}),
      body,
      ...(template.tips ? { tips } : {}),
      ...(template.key === "welcome" ? { linkUrl: link } : {}),
    }),
    [enabled, delay, subject, body, tips, link, template],
  );
  const problem = autoEmailProblem(template, form);
  const changed =
    enabled !== template.enabled ||
    (template.hasDelay && Number(delay) !== template.delayDays) ||
    subject !== template.subject ||
    body !== template.body ||
    link !== (template.linkUrl || "") ||
    JSON.stringify(tips) !== JSON.stringify(template.tips || {});

  // Placeholders go where the cursor last was (subject, text or a tip).
  const insert = (placeholder: string) => {
    const value = `[${placeholder}]`;
    const target = lastFocus.current;
    const el =
      target.kind === "subject"
        ? subjectRef.current
        : target.kind === "tip"
        ? tipRefs.current[target.key]
        : bodyRef.current;
    if (!el) return;
    const current = el.value;
    const next = insertAt(
      current,
      el.selectionStart ?? current.length,
      el.selectionEnd ?? current.length,
      value,
    );
    if (target.kind === "subject") setSubject(next.text);
    else if (target.kind === "tip")
      setTips((t) => ({ ...t, [target.key]: next.text }));
    else setBody(next.text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const doPreview = async () => {
    try {
      const result = await runPreview({
        key: template.key,
        body: { ...form, ...(previewFirm ? { orgId: previewFirm } : {}) },
      }).unwrap();
      setPreview(result);
      // Owner 2026-10-29: show the email itself, not only its words.
      if (result.html) setShowEmail(true);
    } catch (e) {
      toast(errorText(e, "The preview couldn't be made."), { type: "error" });
    }
  };
  useEffect(() => {
    setPreview(null);
  }, [previewFirm]);

  const doTest = async () => {
    try {
      const sent = await sendTest({ key: template.key, body: form }).unwrap();
      toast(`Test email sent to ${sent.to}.`, { type: "success" });
    } catch (e) {
      toast(errorText(e, "The test email wasn't sent."), { type: "error" });
    }
  };

  const doSave = async () => {
    if (problem) return;
    try {
      await save({ key: template.key, body: form }).unwrap();
      toast("Saved. Emails from now on use this text.", { type: "success" });
    } catch (e) {
      toast(errorText(e, "Couldn't save. Please try again."), {
        type: "error",
      });
    }
  };

  const placeholders = template.placeholders.filter(
    (p) => template.key === "checkin" || p !== "Next step tip",
  );

  return (
    <div className={PAGE}>
      <div className={PAGE_INNER}>
        <div className={"flex flex-wrap items-end justify-between gap-3"}>
          <div>
            <Link
              to={"/support/automatic-emails"}
              className={
                "text-[13px] font-semibold text-[#6B7185] hover:text-[#6B4EFF]"
              }>
              ‹ Automatic emails
            </Link>
            <h1
              className={
                "mb-1 mt-2 text-[24px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
              }>
              {template.name}
            </h1>
            <div className={"text-[13.5px] text-[#5D6475]"}>
              {template.when}
            </div>
          </div>
          <div className={"flex flex-wrap gap-2"}>
            <button
              type={"button"}
              disabled={isPreviewing || !!problem}
              onClick={() => void doPreview()}
              className={SMALL_BUTTON}>
              {isPreviewing ? "Preparing…" : "Preview"}
            </button>
            {!template.draftOnly && template.key !== "rating" && (
              <button
                type={"button"}
                disabled={isTesting || !!problem}
                onClick={() => void doTest()}
                className={SMALL_BUTTON}>
                {isTesting ? "Sending…" : "Send me a test"}
              </button>
            )}
            <button
              type={"button"}
              disabled={isSaving || !!problem || !changed}
              onClick={() => void doSave()}
              className={PRIMARY_BUTTON}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
        {problem && (
          <div className={"mt-2 text-[13px] text-[#B42318]"}>{problem}</div>
        )}

        <div
          className={
            "mt-4 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_400px]"
          }>
          <section
            className={
              "flex h-fit flex-col gap-3.5 rounded-[14px] border border-[#EDEFF3] bg-white px-5 py-5"
            }>
            <div
              className={
                "flex flex-wrap items-center gap-4 text-[14px] font-semibold text-[#1F2433]"
              }>
              {template.hasDelay && (
                <label className={"flex items-center gap-2"}>
                  {template.key === "nudge" ? "After" : "Send"}
                  <input
                    value={delay}
                    inputMode={"numeric"}
                    onChange={(e) =>
                      setDelay(
                        e.target.value.replace(/[^0-9]/g, "").slice(0, 2),
                      )
                    }
                    aria-label={"Days"}
                    className={
                      "h-9 w-14 rounded-lg border border-[#DCE2EA] text-center"
                    }
                  />
                  {template.key === "nudge"
                    ? "days without an answer"
                    : template.key === "demo_followup"
                    ? "days after you set the demo to “Demo completed”"
                    : "days after sign-up"}
                </label>
              )}
              <span className={"flex-1"} />
              <label className={"flex items-center gap-2"}>
                <input
                  type={"checkbox"}
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className={"h-[18px] w-[18px] accent-[#6B4EFF]"}
                />
                On
              </label>
            </div>
            {(template.draftOnly || ONBOARDING_KEYS.includes(template.key)) && (
              <div
                className={
                  "rounded-lg bg-[#F5F2FF] px-3 py-2 text-[13px] text-[#4B32D6]"
                }>
                {template.key === "demo_followup"
                  ? "Never sent by itself: the AI rewrites this from your notes on the demo's work item, and the draft waits in the Help desk for you to check and send. This text sets the tone, sign-off and links."
                  : "Never sent by itself: it becomes a draft in the Help desk for you to check, change and send."}
              </div>
            )}
            {template.key === "welcome" && (
              <label
                className={
                  "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]"
                }>
                Get Started link (your package)
                <input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder={"https://…"}
                  className={
                    "h-10 rounded-lg border border-[#DCE2EA] px-3 text-[14px] font-normal text-[#1F2433]"
                  }
                />
                <span className={"text-[12px] font-normal text-[#5D6475]"}>
                  Put in [Get Started link] where it should go. Also used by the
                  check-in.
                </span>
              </label>
            )}
            {template.hasSubject && (
              <label
                className={
                  "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]"
                }>
                Subject
                <input
                  ref={subjectRef}
                  value={subject}
                  maxLength={300}
                  onFocus={() => (lastFocus.current = { kind: "subject" })}
                  onChange={(e) => setSubject(e.target.value)}
                  className={
                    "h-10 rounded-lg border border-[#DCE2EA] px-3 text-[15px] font-medium text-[#1F2433]"
                  }
                />
              </label>
            )}
            <div className={"flex flex-col gap-1.5"}>
              <div className={"flex flex-wrap items-center gap-1.5"}>
                <span className={"mr-1.5 text-[13px] font-bold text-[#3A4256]"}>
                  {template.key === "rating" ? "Question" : "Message"}
                </span>
                <span className={"text-[12.5px] text-[#5D6475]"}>Insert:</span>
                {placeholders.map((p) => (
                  <button
                    key={p}
                    type={"button"}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insert(p)}
                    className={
                      "h-7 rounded-full border border-[#DCE2EA] bg-[#F7F8FB] px-2.5 text-[12.5px] font-semibold text-[#3A4256] hover:border-[#6B4EFF]"
                    }>
                    + {p}
                  </button>
                ))}
              </div>
              <label className={"sr-only"} htmlFor={"auto-email-body"}>
                Message
              </label>
              <textarea
                id={"auto-email-body"}
                ref={bodyRef}
                value={body}
                onFocus={() => (lastFocus.current = { kind: "body" })}
                onChange={(e) => setBody(e.target.value)}
                rows={template.key === "rating" ? 2 : 12}
                className={
                  "resize-y rounded-lg border border-[#DCE2EA] px-3.5 py-3 text-[15px] leading-relaxed text-[#1F2433]"
                }
              />
              <div className={"text-[12.5px] text-[#5D6475]"}>
                Words in [brackets] are filled in for each firm or case when the
                email goes out.
              </div>
            </div>
          </section>

          <aside className={"flex flex-col gap-3"}>
            {template.tips && (
              <div
                className={
                  "flex flex-col gap-3 rounded-[14px] border border-[#EDEFF3] bg-white px-5 py-4"
                }>
                <div>
                  <div className={"text-[15px] font-extrabold text-[#1F2433]"}>
                    [Next step tip]
                  </div>
                  <div className={"mt-0.5 text-[13px] text-[#5D6475]"}>
                    ProCharted picks ONE of these: the first step the firm
                    hasn&apos;t done yet.
                  </div>
                </div>
                {TIP_LABELS.map((tip) => (
                  <label
                    key={tip.key}
                    className={
                      "rounded-xl border border-[#EDEFF3] px-3 py-2.5"
                    }>
                    <span className={"text-[12.5px] font-bold text-[#6B4EFF]"}>
                      {tip.label}
                    </span>
                    <textarea
                      ref={(el) => (tipRefs.current[tip.key] = el)}
                      value={tips[tip.key] || ""}
                      onFocus={() =>
                        (lastFocus.current = { kind: "tip", key: tip.key })
                      }
                      onChange={(e) =>
                        setTips((t) => ({ ...t, [tip.key]: e.target.value }))
                      }
                      rows={3}
                      className={
                        "mt-1.5 w-full resize-y rounded-lg border border-[#EDEFF3] px-2.5 py-2 text-[13.5px] leading-normal text-[#1F2433]"
                      }
                    />
                  </label>
                ))}
              </div>
            )}
            <div
              className={
                "rounded-[14px] border border-[#EDEFF3] bg-white px-5 py-4"
              }>
              <div className={"mb-2 text-[15px] font-extrabold text-[#1F2433]"}>
                Preview
              </div>
              {ONBOARDING_KEYS.includes(template.key) &&
                (firms?.firms.length ?? 0) > 0 && (
                  <label
                    className={
                      "mb-2 flex flex-col gap-1 text-[12.5px] font-semibold text-[#5D6475]"
                    }>
                    Fill in with
                    <select
                      value={previewFirm}
                      onChange={(e) => setPreviewFirm(e.target.value)}
                      className={SMALL_BUTTON}>
                      <option value={""}>A sample firm</option>
                      {firms!.firms.slice(0, 30).map((f) => (
                        <option key={f.orgId} value={f.orgId}>
                          {f.firmName}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              {preview ? (
                <div
                  className={
                    "rounded-lg border border-[#EDEFF3] bg-[#FCFCFE] px-3.5 py-3 text-[13.5px] leading-relaxed text-[#1F2433]"
                  }>
                  {preview.subject && (
                    <div className={"mb-2 text-[12.5px] text-[#5D6475]"}>
                      <b className={"text-[#1F2433]"}>Subject:</b>{" "}
                      {preview.subject}
                    </div>
                  )}
                  <div className={"whitespace-pre-wrap break-words"}>
                    {preview.body}
                  </div>
                  {preview.html && (
                    <button
                      type={"button"}
                      onClick={() => setShowEmail(true)}
                      className={classNames(SMALL_BUTTON, "mt-3")}
                      data-testid={"auto-email-see-email"}>
                      👁 See the email
                    </button>
                  )}
                  {showEmail && preview.html && (
                    <EmailPreviewModal
                      preview={{
                        to: preview.to,
                        subject: preview.subject,
                        html: preview.html,
                      }}
                      onClose={() => setShowEmail(false)}
                      note={
                        "Filled in with the names above. Nothing has been sent."
                      }
                    />
                  )}
                </div>
              ) : (
                <div className={"text-[13px] text-[#5D6475]"}>
                  Click Preview to see it with real names filled in.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
